import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { api, WHATSAPP_NUMBER } from "@/lib/api";
import { track, trackLeadGenerated } from "@/lib/analytics";
import { getAttribution } from "@/lib/attribution";
import SEOHead from "@/components/SEOHead";
import LeadCaptureModal from "@/components/LeadCaptureModal";
import ProductCheckout from "@/components/ProductCheckout";
import AIFitShareCard from "@/components/RealTalk/AIFitShareCard";
import { toast } from "sonner";
import {
  Bot, ArrowRight, ArrowLeft, Sparkles, ShieldCheck, MapPin, Loader2,
  MessageCircle, GraduationCap, UserCheck, FileText, CheckCircle2, Phone,
  AlertTriangle, ShieldAlert,
} from "lucide-react";

const COURSES = ["B.Tech", "MBA", "BBA", "B.Com", "B.Sc", "B.A", "MBBS", "BDS", "BA LLB", "BCA", "B.Arch", "B.Pharm"];
const STATES = ["Delhi", "Maharashtra", "Karnataka", "Tamil Nadu", "Uttar Pradesh", "Telangana", "Gujarat", "West Bengal", "Punjab", "Rajasthan"];

export default function AICounseling() {
  const [step, setStep] = useState(1); // 1 prefs, 2 contact, 3 result
  const [form, setForm] = useState({ course: "", state: "", marks: "", budget: "", name: "", mobile: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [checkoutKey, setCheckoutKey] = useState(null); // "REGRET_RISK_99" | "EXPERT_CALL_499" | "DECISION_PACK_999"
  const [aiDown, setAiDown] = useState(false);
  const [fallbackOpen, setFallbackOpen] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const started = useRef(false);
  useEffect(() => { if (!started.current) { started.current = true; track("ai_chat_started", { source: "ai_counseling_page" }); } }, []);

  const getRecommendations = async () => {
    if (!form.name.trim() || !/^\d{10}$/.test(form.mobile.trim())) {
      toast.error("Please enter your name and a valid 10-digit mobile number."); return;
    }
    setLoading(true);
    setAiDown(false);
    // Fire ONLY on real submission (validated input + about to call the API),
    // not on page open. No PII sent — only non-identifying context.
    track("ai_counsellor_started", { course: form.course, state: form.state });
    try {
      const { data } = await api.post("/ai/recommend", { ...form, attribution: getAttribution() });
      setResult(data);
      track("ai_step_2_completed", { course: form.course, state: form.state });
      track("recommendation_generated", {
        course: form.course,
        state: form.state,
        preferred_state: form.state,
        budget: form.budget || undefined,
        recommendations_count: data.recommendations.length,
      });
      trackLeadGenerated(
        { id: data.leadId, source: "ai_counseling", isNewLead: true },
        {
          course: form.course,
          state: form.state,
          lead_type: "ai_counselling_form",
          form_name: "AI Admission Counsellor",
        },
      );
      // Spec-compliant conversion event. Fires ONLY after:
      //   (a) name is present and mobile passed the 10-digit regex above,
      //   (b) backend has returned a lead id (persisted in DB),
      //   (c) recommendation results are about to render (setStep(3) below).
      // page_path + UTM/source parameters are auto-added by commonParams() in track().
      track("lead_submitted", {
        course: form.course,
        preferred_state: form.state,
        budget: form.budget || undefined,
        lead_id: data.leadId,
        source: "ai_counsellor",
      });
      track("ai_fit_check_completed", {
        course: form.course,
        preferred_state: form.state,
        budget: form.budget || undefined,
        recommendations_count: data.recommendations.length,
      });
      track("ai_step_3_completed", { course: form.course, state: form.state });
      setStep(3);
    } catch (e) {
      const status = e.response?.status;
      if (status === 429 || status === 503) {
        setAiDown(true); // honest fallback — never auto-retry the AI request
        track("ai_unavailable_shown", { status });
      } else {
        toast.error("Could not fetch recommendations. Please try again.");
      }
    }
    finally { setLoading(false); }
  };

  const openCheckout = (key) => { track("product_selected", { product_key: key, location: "ai_result" }); setCheckoutKey(key); };

  const waHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hi CollegeUnlock, I'm ${form.name}. I just got AI college recommendations for ${form.course || "my course"} and want admission guidance.`)}`;

  return (
    <>
      <SEOHead title="AI Admission Counsellor" description="Get instant, AI-matched college recommendations from India's verified college database and talk to an admission expert." path="/ai-counseling" />
      <div className="min-h-[70vh] bg-gradient-to-br from-[#0B2352] to-[#123072] py-10 sm:py-14">
        <div className="mx-auto max-w-2xl px-4">
          <div className="text-center text-white">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold ring-1 ring-white/20"><Bot className="h-3.5 w-3.5 text-[#E9C877]" /> AI Admission Counsellor</span>
            <h1 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold">Find your best-fit colleges in 30 seconds</h1>
            <p className="mt-2 text-sm text-white/70">Answer a few questions. Get verified, AI-matched colleges. Talk to an expert.</p>
          </div>

          <div className="mt-7 rounded-2xl border border-white/10 bg-white p-6 sm:p-7 shadow-2xl">
            {step === 1 && (
              <div data-testid="ai-step-prefs">
                <Progress step={1} />
                <h2 className="font-display text-lg font-bold text-slate-900">Your preferences</h2>
                <div className="mt-4 grid gap-3">
                  <Field label="Course / Programme">
                    <select data-testid="ai-course" value={form.course} onChange={set("course")} className={inp}><option value="">Select a course</option>{COURSES.map((c) => <option key={c}>{c}</option>)}</select>
                  </Field>
                  <Field label="Preferred State">
                    <select data-testid="ai-state" value={form.state} onChange={set("state")} className={inp}><option value="">Any state</option>{STATES.map((s) => <option key={s}>{s}</option>)}</select>
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Marks / Rank"><input data-testid="ai-marks" value={form.marks} onChange={set("marks")} placeholder="e.g. 85% or Rank 5000" className={inp} /></Field>
                    <Field label="Budget / year"><input data-testid="ai-budget" value={form.budget} onChange={set("budget")} placeholder="e.g. 3 LPA" className={inp} /></Field>
                  </div>
                </div>
                <button data-testid="ai-next" onClick={() => { track("ai_step_1_completed", { course: form.course, state: form.state }); setStep(2); }} className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg bg-[#0B2A6B] py-3 text-sm font-bold text-white hover:bg-[#081E4D]">Next <ArrowRight className="h-4 w-4" /></button>
              </div>
            )}

            {step === 2 && (
              <div data-testid="ai-step-contact">
                <Progress step={2} />
                <h2 className="font-display text-lg font-bold text-slate-900">Where should we send your matches?</h2>
                <p className="mt-1 text-xs text-slate-500">We'll show your colleges instantly and a counsellor may reach out.</p>
                <div className="mt-4 grid gap-3">
                  <Field label="Full Name*"><input data-testid="ai-name" value={form.name} onChange={set("name")} placeholder="Your name" className={inp} /></Field>
                  <Field label="Mobile Number*"><input data-testid="ai-mobile" value={form.mobile} onChange={set("mobile")} placeholder="10-digit mobile" maxLength={10} className={inp} /></Field>
                  <Field label="Email (optional)"><input data-testid="ai-email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inp} /></Field>
                </div>
                <div className="mt-5 flex gap-2">
                  <button onClick={() => setStep(1)} className="flex items-center justify-center gap-1 rounded-lg border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-600 hover:bg-slate-50"><ArrowLeft className="h-4 w-4" /> Back</button>
                  <button data-testid="ai-get-recommendations" onClick={getRecommendations} disabled={loading} className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#D4A24C] py-3 text-sm font-bold text-white hover:bg-[#C08E3B] disabled:opacity-60">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Show My Colleges</button>
                </div>
              </div>
            )}

            {aiDown && (
              <div data-testid="ai-unavailable-panel" className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
                <p className="flex items-center gap-2 text-sm font-semibold text-amber-900">
                  <AlertTriangle className="h-4 w-4 shrink-0" /> AI counselling is temporarily unavailable.
                </p>
                <p className="mt-1 text-xs text-amber-800">Your details were not lost. A human admission expert can guide you with verified college options right now.</p>
                <button
                  data-testid="ai-fallback-expert-cta"
                  onClick={() => { track("cta_click", { cta: "ai_fallback_expert", location: "ai_unavailable" }); setFallbackOpen(true); }}
                  className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[#0B2A6B] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#081E4D]"
                >
                  <UserCheck className="h-4 w-4" /> Connect with an Admission Expert
                </button>
              </div>
            )}

            {step === 3 && result && (
              <div data-testid="ai-step-result">
                <Progress step={3} />
                {result.aiDegraded && (
                  <p data-testid="ai-degraded-notice" className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">
                    AI counselling is temporarily unavailable — showing your verified matches below.
                  </p>
                )}
                <div className="rounded-xl bg-[#0B2352]/[0.04] p-4 ring-1 ring-[#0B2352]/10">
                  <p className="flex items-start gap-2 text-sm text-slate-700"><Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[#D4A24C]" /> {result.summary}</p>
                </div>

                <RegretRiskScore score={riskFromLead(result.leadScore)} onBook={() => openCheckout("REGRET_RISK_99")} />

                <h2 className="mt-5 font-display text-lg font-bold text-slate-900">Your matched colleges</h2>
                <div className="mt-3 space-y-2.5">
                  {result.recommendations.map((r) => (
                    <a key={r.collegeId} href={`/college/${r.slug}`} data-testid={`ai-rec-${r.collegeId}`} className="block rounded-xl border border-slate-200 p-3.5 transition-colors hover:border-[#D4A24C]/50 hover:bg-slate-50">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="flex items-center gap-1.5 font-display text-sm font-bold text-slate-900"><GraduationCap className="h-4 w-4 text-[#0B2A6B]" /> {r.name}</p>
                          <p className="mt-1 text-xs text-slate-500"><MapPin className="mr-1 inline h-3 w-3" />{[r.city, r.state].filter(Boolean).join(", ")}</p>
                          <p className="mt-1.5 text-[13px] text-slate-600">{r.rationale}</p>
                        </div>
                        <span className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-emerald-600"><ShieldCheck className="h-3.5 w-3.5" /> Verified</span>
                      </div>
                    </a>
                  ))}
                </div>

                {/* Product ladder CTAs — exact CTO strings */}
                <div className="mt-6 space-y-3">
                  <button data-testid="cta-regret-report" onClick={() => openCheckout("REGRET_RISK_99")} className="flex w-full items-center justify-between rounded-xl border-2 border-[#D4A24C] bg-[#FDF9F1] px-5 py-4 text-left transition-transform hover:-translate-y-0.5">
                    <span className="flex items-center gap-3"><FileText className="h-6 w-6 text-[#C08E3B]" /><span><span className="block text-sm font-bold text-slate-900">Get My ₹99 College Regret Risk Report</span><span className="block text-xs text-slate-500">Instant PDF · verified data · low-friction</span></span></span>
                    <ArrowRight className="h-5 w-5 text-[#C08E3B]" />
                  </button>
                  <button data-testid="cta-expert-call" onClick={() => openCheckout("EXPERT_CALL_499")} className="flex w-full items-center justify-between rounded-xl bg-gradient-to-r from-[#0B2A6B] to-[#13367f] px-5 py-4 text-left text-white shadow-lg transition-transform hover:-translate-y-0.5">
                    <span className="flex items-center gap-3"><UserCheck className="h-6 w-6 text-[#E9C877]" /><span><span className="block text-sm font-bold">Book Expert Admission Call – ₹499</span><span className="block text-xs text-white/70">20-min 1-on-1 · pick your slot</span></span></span>
                    <ArrowRight className="h-5 w-5" />
                  </button>
                  <button data-testid="cta-decision-pack" onClick={() => openCheckout("DECISION_PACK_999")} className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-5 py-3 text-left hover:border-[#0B2A6B]/40">
                    <span className="flex items-center gap-3"><Sparkles className="h-5 w-5 text-[#0B2A6B]" /><span><span className="block text-sm font-bold text-slate-900">Get My Admission Decision Pack – ₹999</span><span className="block text-xs text-slate-500">30-min call + written admission plan (PDF within 24h)</span></span></span>
                    <ArrowRight className="h-5 w-5 text-slate-400" />
                  </button>
                  <a data-testid="cta-whatsapp" href={waHref} target="_blank" rel="noreferrer" onClick={() => track("whatsapp_click", { location: "ai_result" })} className="flex w-full items-center justify-center gap-2 rounded-xl border border-[#25D366] bg-[#25D366]/5 py-3 text-sm font-bold text-[#128C3E] hover:bg-[#25D366]/10"><MessageCircle className="h-4 w-4" /> Chat on WhatsApp</a>
                </div>
                {/* Shareable card generated from the REAL AI Fit Check output. */}
                <AIFitShareCard result={result} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Honest AI-unavailable fallback: lead only on explicit form submit + consent */}
      <LeadCaptureModal open={fallbackOpen} intent="expert_connect" onClose={() => setFallbackOpen(false)} />

      {/* Product checkout (₹99 / ₹499 / ₹999) */}
      <ProductCheckout
        open={!!checkoutKey}
        productKey={checkoutKey || "REGRET_RISK_99"}
        onClose={() => setCheckoutKey(null)}
        defaultProfile={{ name: form.name, mobile: form.mobile, email: form.email, courseInterest: form.course }}
        leadId={result?.leadId}
      />
    </>
  );
}

const inp = "w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-[#0B2A6B]";
function Field({ label, children }) { return <label className="block"><span className="text-xs font-semibold text-slate-600">{label}</span><div className="mt-1">{children}</div></label>; }

// Presentation-only mapping of the existing backend leadScore (profile completeness/alignment, ~70-100)
// into a "College Regret Risk Score" (0-100). No backend / AI algorithm change. Higher alignment = lower risk.
function riskFromLead(leadScore) {
  const ls = Math.max(70, Math.min(100, Number(leadScore) || 70));
  const risk = Math.round(65 - (ls - 70) * (65 - 12) / 30);
  return Math.max(8, Math.min(92, risk));
}

const RISK_BANDS = [
  {
    max: 30, key: "low", label: "Low College Regret Risk", color: "#16A34A",
    ring: "ring-emerald-200", tint: "bg-emerald-50", Icon: ShieldCheck,
    body: "You are currently making a well-aligned admission decision.",
    rec: "Optional expert counselling is available for additional confidence.",
  },
  {
    max: 60, key: "moderate", label: "Moderate College Regret Risk", color: "#D4A24C",
    ring: "ring-amber-200", tint: "bg-amber-50", Icon: AlertTriangle,
    body: "There are a few areas where your current college choice may not fully match your profile or goals.",
    rec: "Recommendation: Book a Human Expert Counselling Session.",
  },
  {
    max: 100, key: "high", label: "High College Regret Risk", color: "#DC2626",
    ring: "ring-red-200", tint: "bg-red-50", Icon: ShieldAlert,
    body: "Your current admission choices may carry a significant risk of future dissatisfaction due to factors such as course alignment, budget, career expectations, location, or overall fit.",
    rec: "Strong Recommendation: Speak with a Human Career Expert before paying admission fees.",
  },
];

function RegretRiskScore({ score, onBook }) {
  const band = RISK_BANDS.find((b) => score <= b.max) || RISK_BANDS[RISK_BANDS.length - 1];
  const { Icon } = band;
  return (
    <div data-testid="regret-risk-section" className={`mt-5 rounded-2xl ${band.tint} p-5 ring-1 ${band.ring}`}>
      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">College Regret Risk Score™</p>
      <div className="mt-3 flex items-center gap-4">
        <div className="relative flex h-24 w-24 shrink-0 items-center justify-center rounded-full"
          style={{ background: `conic-gradient(${band.color} ${score * 3.6}deg, #E2E8F0 0deg)` }}>
          <div className="flex h-[76px] w-[76px] flex-col items-center justify-center rounded-full bg-white">
            <span data-testid="regret-risk-score" className="font-display text-2xl font-extrabold leading-none" style={{ color: band.color }}>{score}</span>
            <span className="text-[9px] font-semibold text-slate-400">/ 100</span>
          </div>
        </div>
        <div className="min-w-0">
          <p data-testid="regret-risk-band" className="flex items-center gap-1.5 font-display text-base font-bold" style={{ color: band.color }}>
            <Icon className="h-4 w-4 shrink-0" /> {band.label}
          </p>
          <p className="mt-1.5 text-[13px] leading-snug text-slate-700">{band.body}</p>
          <p className="mt-1.5 text-[13px] font-semibold leading-snug text-slate-800">{band.rec}</p>
        </div>
      </div>

      <div className="mt-4 rounded-xl bg-white p-4 ring-1 ring-slate-100">
        <p className="font-display text-sm font-bold text-slate-900">Avoid an expensive admission mistake.</p>
        <p className="mt-1 text-[13px] text-slate-600">
          Get a <span className="font-semibold text-slate-900 line-through decoration-slate-400">₹5,000</span> Human Career Counselling Session for only <span className="font-extrabold text-[#0B2A6B]">₹99</span>.
        </p>
        <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wide text-[#D4A24C]">First 100 students only</p>
        <button data-testid="regret-risk-cta" onClick={onBook}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#0B2A6B] to-[#13367f] px-5 py-3.5 text-sm font-bold text-white shadow-lg transition-transform hover:-translate-y-0.5">
          <UserCheck className="h-5 w-5 text-[#E9C877]" /> Claim ₹99 Expert Session <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function Progress({ step }) {
  const labels = { 1: "25% • Your preferences", 2: "50% • Almost there", 3: "100% • Done 🎉" };
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2">
        {[1, 2, 3].map((i) => <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-[#D4A24C]" : "bg-slate-200"}`} />)}
      </div>
      <p className="mt-1.5 text-[11px] font-semibold text-slate-400" data-testid={`ai-progress-label-${step}`}>{labels[step]}</p>
    </div>
  );
}
